import React, { useEffect, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import ApiService from '../../services/ApiService';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { formatCurrency } from '../../utils/helper';
import { getGenericStatus } from '../../utils/statusHelper';

const List = () => {
    const [checkupTypes, setCheckupTypes] = useState([]);
    const [isLoader, setIsLoader] = useState(false);
    const [pagination, setPagination] = useState({});
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');

    useEffect(() => {
        getData(page, search);
    }, [page, search]);

    const getData = async (p = 1, s = '') => {
        try {
            setIsLoader(true);
            const response = await ApiService.request({
                method: 'GET',
                url: `/admin/checkup-types?page=${p}&search=${s}`
            });
            if (response.data.status) {
                setCheckupTypes(response.data.data.data);
                setPagination(response.data.data);
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error('Failed to fetch checkup types');
        } finally {
            setIsLoader(false);
        }
    };

    const handleDelete = async (id) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await ApiService.request({
                        method: 'DELETE',
                        url: `/admin/checkup-types/${id}`
                    });
                    if (response.data.status) {
                        toast.success(response.data.message);
                        getData(page, search);
                    } else {
                        toast.error(response.data.message);
                    }
                } catch (error) {
                    toast.error('Failed to delete');
                }
            }
        });
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.last_page) {
            setPage(newPage);
        }
    };

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                            Checkup Types
                        </h1>
                    </div>
                    <div className="d-flex align-items-center gap-2 gap-lg-3">
                        <OverlayTrigger placement="top" overlay={<Tooltip>Create New Checkup Type</Tooltip>}>
                            <Link to="/checkup-types/create" className="btn btn-sm fw-bold btn-base">
                                Add Checkup Type
                            </Link>
                        </OverlayTrigger>
                    </div>
                </div>
            </div>

            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-fluid">
                    <div className="card">
                        <div className="card-header border-0 pt-6">
                            <div className="card-title">
                                <div className="d-flex align-items-center position-relative my-1">
                                    <span className="svg-icon svg-icon-1 position-absolute ms-6">
                                        <i className="fas fa-search"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control form-control-solid w-250px ps-15"
                                        placeholder="Search Checkup Types"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="card-body py-4">
                            {isLoader ? (
                                <div className="d-flex justify-content-center">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table align-middle table-row-dashed fs-6 gy-5">
                                        <thead>
                                            <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                                                <th className="min-w-125px">Title</th>
                                                <th className="min-w-125px">Price</th>
                                                <th className="min-w-125px">Status</th>
                                                <th className="text-end min-w-100px">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="text-gray-600 fw-semibold">
                                            {checkupTypes.map((item) => (
                                                <tr key={item.id}>
                                                    <td>
                                                        <div className="d-flex flex-column">
                                                            <span className="text-gray-800 mb-1">{item.title}</span>
                                                            <span className="text-muted fs-7">{item.description}</span>
                                                        </div>
                                                    </td>
                                                    <td>{formatCurrency(item.price)}</td>
                                                    <td>
                                                        {(() => {
                                                            const { badgeClass, label } = getGenericStatus(item.status);
                                                            return (
                                                                <div className={`badge ${badgeClass} fw-bold`}>
                                                                    {label}
                                                                </div>
                                                            );
                                                        })()}
                                                    </td>
                                                    <td className="text-end">
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Edit Checkup Type</Tooltip>}>
                                                            <Link to={`/checkup-types/edit/${item.id}`} className="btn btn-icon btn-light btn-active-light-primary btn-sm me-2">
                                                                <i className="fas fa-edit"></i>
                                                            </Link>
                                                        </OverlayTrigger>
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Delete Checkup Type</Tooltip>}>
                                                            <button onClick={() => handleDelete(item.id)} className="btn btn-icon btn-light btn-active-light-danger btn-sm">
                                                                <i className="fas fa-trash"></i>
                                                            </button>
                                                        </OverlayTrigger>
                                                    </td>
                                                </tr>
                                            ))}
                                            {checkupTypes.length === 0 && (
                                                <tr>
                                                    <td colSpan="4" className="text-center">No checkup types found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            
                            {/* Pagination */}
                            {pagination.last_page > 1 && (
                                <div className="d-flex justify-content-end pt-10">
                                    <ul className="pagination">
                                        <li className={`page-item ${pagination.current_page === 1 ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => handlePageChange(pagination.current_page - 1)}>Previous</button>
                                        </li>
                                        {[...Array(pagination.last_page)].map((_, i) => (
                                            <li key={i} className={`page-item ${pagination.current_page === i + 1 ? 'active' : ''}`}>
                                                <button className="page-link" onClick={() => handlePageChange(i + 1)}>{i + 1}</button>
                                            </li>
                                        ))}
                                        <li className={`page-item ${pagination.current_page === pagination.last_page ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => handlePageChange(pagination.current_page + 1)}>Next</button>
                                        </li>
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default List;
